import { useCallback, useEffect, useMemo, useState } from 'react'
import { mutate as mutateGlobal } from 'swr'
import { cadinuProfileAbi } from 'config/abi/cadinuProfile'
import { useProfile } from 'state/profile/hooks'
import { getCadinuProfileAddress } from 'utils/addressHelpers'
import { useContractRead } from 'wagmi'
import { PROFILE_CHAIN_ID, txLabel, useChainTx } from './chainTx'
import { ActionBtn, Hint, Icon, Note } from './Identity'
import { ERC721_APPROVE_ABI, HeldNft, collectionInfo, nftApprovedForProfile, useHeldCadinuNfts, useOnchainProfile } from './onchainReads'
import { pointsDelta, pointsText } from './onchainMath'
import { Check, Danger, NftPicker, Select, Step, Steps, TxStatus, Warn, pickKey } from './onchainUi'
import { Tag } from './ui'

type Kind = 'register' | 'change' | 'reactivate'
/** The legacy calls, unchanged: ConfirmProfileCreationModal (createProfile), ChangeProfilePicView (updateProfile / reactivateProfile). */
const CALL: Record<Kind, 'createProfile' | 'updateProfile' | 'reactivateProfile'> = { register: 'createProfile', change: 'updateProfile', reactivate: 'reactivateProfile' }
const CONFIRM: Record<Kind, string> = { register: 'Register on-chain', change: 'Lock the new NFT', reactivate: 'Reactivate profile' }
const DONE: Record<Kind, string> = { register: 'Registered on BNB Chain.', change: 'Picture changed on BNB Chain.', reactivate: 'Profile reactivated.' }

/** Choose a held NFT → approve it for the profile contract (ERC-721 approve, the legacy "Enable" step) → the profile call. */
function LockFlow({ kind, wallet, nfts, current, currentLevel = 0, levelPoint, onDone, onBuy }: { kind: Kind; wallet: `0x${string}`; nfts: HeldNft[] | undefined; current?: string | null; currentLevel?: number; levelPoint?: bigint; onDone: (message: string, hash: string) => void; onBuy: () => void }) {
  const choices = useMemo(() => nfts ?? [], [nfts])
  const [sel, setSel] = useState<HeldNft | null>(null)
  const [tokenId, setTokenId] = useState<bigint | undefined>(undefined)
  const [approved, setApproved] = useState<boolean | undefined>(undefined)
  const approve = useChainTx()
  const confirm = useChainTx()
  const delta = sel && levelPoint !== undefined ? pointsDelta(levelPoint, kind === 'change' ? currentLevel : 0, sel.level) : undefined
  const check = useCallback(async () => {
    if (!sel || tokenId === undefined) return
    setApproved(undefined)
    try {
      setApproved(await nftApprovedForProfile(sel.collection, tokenId, wallet))
    } catch {
      setApproved(false)
    }
  }, [sel, tokenId, wallet])
  useEffect(() => {
    check()
  }, [check])
  useEffect(() => {
    if (!sel && choices.length === 1) {
      setSel(choices[0])
      setTokenId(choices[0].tokenIds[0])
    }
  }, [sel, choices])
  const busy = approve.busy || confirm.busy
  const done = confirm.state === 'success'
  if (!nfts) return <Note aria-busy="true">Reading your NFTs on BNB Chain…</Note>
  if (!choices.length)
    return (
      <div style={{ display: 'grid', gap: 10 }} data-testid="onchain-no-nft">
        <Note>{kind === 'change' ? 'This wallet holds no other CADINU NFT to lock.' : 'Locking needs a CADINU Identity Arts NFT in this wallet.'}</Note>
        <ActionBtn type="button" $primary onClick={onBuy} data-testid="onchain-buy" style={{ justifySelf: 'start' }}>
          <Icon name="image" size={18} />
          Buy a CADINU NFT
        </ActionBtn>
      </div>
    )
  return (
    <Steps>
      <Step n={1} title="Choose the NFT to lock" state={sel ? 'done' : 'now'} testId="step-choose">
        <NftPicker
          nfts={choices}
          selected={sel ? pickKey(sel) : null}
          disabled={busy || done}
          onSelect={(n) => {
            setSel(n)
            setTokenId(n.tokenIds[0])
            approve.reset()
            confirm.reset()
          }}
        />
        {sel && tokenId !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Note>
              Locks <strong style={{ textTransform: 'capitalize' }}>{sel.character}</strong> (level {sel.level})
            </Note>
            {sel.tokenIds.length > 1 ? (
              <Select
                aria-label={`Which ${sel.character} (${sel.tokenIds.length} held)`}
                value={String(tokenId)}
                disabled={busy || done}
                onChange={(e) => {
                  setTokenId(BigInt(e.target.value))
                  approve.reset()
                }}
                data-testid="pick-token"
              >
                {sel.tokenIds.map((id) => (
                  <option key={String(id)} value={String(id)}>
                    #{String(id)}
                  </option>
                ))}
              </Select>
            ) : (
              <strong style={{ fontSize: 14 }}>#{String(tokenId)}</strong>
            )}
          </div>
        )}
      </Step>
      <Step n={2} title="Approve the NFT" state={!sel ? 'todo' : approved ? 'done' : 'now'} testId="step-approve">
        {sel && approved === undefined && <Note aria-busy="true">Checking the approval on BNB Chain…</Note>}
        {sel && approved === false && (
          <>
            <Note>Lets the profile contract take this one NFT. Network fee in BNB only.</Note>
            <ActionBtn
              type="button"
              $primary
              disabled={busy}
              data-testid="approve-nft"
              onClick={async () => {
                if (await approve.run({ address: sel.collection, abi: ERC721_APPROVE_ABI, functionName: 'approve', args: [getCadinuProfileAddress(), tokenId] })) await check()
              }}
              style={{ justifySelf: 'start' }}
            >
              {txLabel(approve.state, 'Approve NFT', 'Approved')}
            </ActionBtn>
          </>
        )}
        {sel && approved && <Note>Approved.</Note>}
        <TxStatus state={approve.state} error={approve.error} hash={approve.hash} done="Approved." testId="approve-status" />
      </Step>
      <Step n={3} title={CONFIRM[kind]} state={done ? 'done' : sel && approved ? 'now' : 'todo'} testId="step-confirm">
        {kind === 'register' && <Note>No CBON cost: the NFT is locked and your network fee is paid in BNB.</Note>}
        {kind === 'change' && current && <Note>{current} goes back to your wallet.</Note>}
        {delta !== undefined && (
          <Note data-testid="points-effect" style={{ color: delta < 0n ? '#ffd27a' : delta > 0n ? '#8ff0c4' : undefined }}>
            {delta === 0n ? 'No change to your points (same level).' : `${pointsText(delta)}: the level points of the locked NFT (${Number(levelPoint).toLocaleString('en-US')} per level).`}
          </Note>
        )}
        <ActionBtn
          type="button"
          $primary
          disabled={!sel || !approved || busy || done}
          data-testid="confirm-onchain"
          onClick={async () => {
            if (!sel || tokenId === undefined) return
            const h = await confirm.run({ address: getCadinuProfileAddress(), abi: cadinuProfileAbi, functionName: CALL[kind], args: [sel.collection, tokenId] })
            if (h) onDone(DONE[kind], h)
          }}
          style={{ justifySelf: 'start' }}
        >
          {txLabel(confirm.state, CONFIRM[kind], DONE[kind].replace('.', ''))}
        </ActionBtn>
        {!approved && sel && <Note>Approve the NFT first.</Note>}
        <TxStatus state={confirm.state} error={confirm.error} hash={confirm.hash} done={DONE[kind]} testId="confirm-status" />
      </Step>
    </Steps>
  )
}

/** pauseProfile (legacy PauseProfileView): the locked NFT returns to the wallet; confirmed with "I understand". */
function PauseFlow({ current, levelPoints, onDone }: { current: string; levelPoints?: bigint; onDone: (message: string, hash: string) => void }) {
  const delta = levelPoints !== undefined ? -levelPoints : undefined
  const [ok, setOk] = useState(false)
  const tx = useChainTx()
  return (
    <div style={{ display: 'grid', gap: 12 }} data-testid="pause-flow">
      <Danger>This suspends your on-chain profile and sends {current} back to your wallet.</Danger>
      <Note>While it is paused you do not earn points. Your other points stay with your profile. Network fee in BNB only.</Note>
      {delta !== undefined && (
        <Note data-testid="points-effect" style={{ color: '#ffd27a' }}>
          {pointsText(delta)} now: the level points of the locked NFT. They come back when you reactivate.
        </Note>
      )}
      <Check htmlFor="pause-ack">
        <input id="pause-ack" type="checkbox" checked={ok} disabled={tx.busy || tx.state === 'success'} onChange={(e) => setOk(e.target.checked)} data-testid="pause-ack" />I understand
      </Check>
      <ActionBtn
        type="button"
        disabled={!ok || tx.busy || tx.state === 'success'}
        data-testid="confirm-pause"
        style={{ justifySelf: 'start', borderColor: 'rgba(255,120,120,0.55)', color: '#ffb4b4' }}
        onClick={async () => {
          const h = await tx.run({ address: getCadinuProfileAddress(), abi: cadinuProfileAbi, functionName: 'pauseProfile' })
          if (h) onDone('Paused. The NFT is back in your wallet.', h)
        }}
      >
        {txLabel(tx.state, 'Pause profile', 'Paused')}
      </ActionBtn>
      <TxStatus state={tx.state} error={tx.error} hash={tx.hash} done="Paused. The NFT is back in your wallet." testId="pause-status" />
    </div>
  )
}

/**
 * The on-chain CADINU profile, edited in place (Bez, 2026-10-03: "merge it properly"): register (createProfile), change the
 * locked NFT (updateProfile), pause (pauseProfile = unlock) and reactivate (reactivateProfile = lock again). Same contract
 * calls and approve step as the legacy /profile and /create-profile pages; no CBON cost (the CAKE costs were PancakeSwap's).
 */
export default function OnchainEditor({ wallet, onBuy, initial, bare }: { wallet: `0x${string}`; onBuy: () => void; initial?: 'change' | 'pause'; bare?: boolean }) {
  const { profile: chain, refresh } = useOnchainProfile(wallet)
  const { nfts, refresh: refreshNfts } = useHeldCadinuNfts(wallet)
  const { refresh: refreshLegacy } = useProfile()
  const [mode, setMode] = useState<'menu' | 'change' | 'pause'>(initial ?? 'menu')
  const { data: levelPoint } = useContractRead({ address: getCadinuProfileAddress(), abi: cadinuProfileAbi, functionName: 'LEVEL_POINT', chainId: PROFILE_CHAIN_ID })
  const lp = levelPoint === undefined ? undefined : (levelPoint as bigint)
  // after a confirmed change the editor goes back to its menu for the new state and keeps the result on top
  const [notice, setNotice] = useState<{ message: string; hash: string } | null>(null)
  const onDone = useCallback(
    async (message: string, hash: string) => {
      setNotice({ message, hash })
      await refresh()
      setMode('menu')
      refreshNfts()
      refreshLegacy?.()
      mutateGlobal(`nfts:${wallet}`)
    },
    [refresh, refreshNfts, refreshLegacy, wallet],
  )
  const lockedInfo = chain?.collection ? collectionInfo(chain.collection) : null
  const current = lockedInfo ? `${lockedInfo.character.charAt(0).toUpperCase()}${lockedInfo.character.slice(1)} #${String(chain?.tokenId)}` : null
  const state = !chain ? 'loading' : !chain.registered ? 'none' : chain.active ? 'active' : 'paused'
  return (
    <section style={{ display: 'grid', gap: 12 }} data-testid="onchain-editor" data-state={state} aria-labelledby="onchain-h">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Hint label="the on-chain profile" text="The original CADINU profile on BNB Chain: an Identity Arts NFT locked in the profile contract as your on-chain picture, with points and the 10% NFT referral discount. Optional: your CADINU profile works without it.">
          <h3 id="onchain-h" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            {bare ? 'Status' : 'On-chain profile'}
          </h3>
        </Hint>
        {state === 'loading' ? null : state === 'none' ? <Tag>Not registered</Tag> : <Tag $tone={state === 'active' ? 'ok' : 'off'}>{state === 'active' ? 'Active' : 'Paused'}</Tag>}
      </div>
      {state === 'loading' && <Note aria-busy="true">Reading BNB Chain…</Note>}
      {notice && <TxStatus state="success" error={null} hash={notice.hash} done={notice.message} testId="onchain-notice" />}
      {state === 'active' && lockedInfo && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }} data-testid="onchain-locked">
          {lockedInfo.image && <img src={lockedInfo.image} alt="" width={56} height={56} style={{ borderRadius: 14, objectFit: 'cover' }} />}
          <div style={{ display: 'grid', gap: 2 }}>
            <strong style={{ fontSize: 15 }}>{current}</strong>
            <Note>
              Locked · level {chain?.level} · {Number(chain?.points ?? 0n).toLocaleString('en-US')} points
            </Note>
          </div>
        </div>
      )}
      {state === 'active' && mode === 'menu' && (
        <>
          <Warn>Before you change or pause, claim any unspent profile rewards.</Warn>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <ActionBtn
              type="button"
              $primary
              onClick={() => {
                setNotice(null)
                setMode('change')
              }}
              data-testid="onchain-change"
            >
              <Icon name="image" size={18} />
              Change NFT
            </ActionBtn>
            <ActionBtn
              type="button"
              onClick={() => {
                setNotice(null)
                setMode('pause')
              }}
              data-testid="onchain-pause"
            >
              <Icon name="lock" size={18} />
              Pause profile
            </ActionBtn>
          </div>
        </>
      )}
      {state === 'active' && mode === 'change' && <LockFlow kind="change" wallet={wallet} nfts={nfts} current={current} currentLevel={chain?.level} levelPoint={lp} onDone={onDone} onBuy={onBuy} />}
      {state === 'active' && mode === 'pause' && current && <PauseFlow current={current} levelPoints={lp !== undefined && chain ? lp * BigInt(chain.level) : undefined} onDone={onDone} />}
      {state === 'active' && mode !== 'menu' && (
        <button type="button" onClick={() => setMode('menu')} style={{ justifySelf: 'start', background: 'none', border: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', minHeight: 44, padding: 0, fontSize: 14 }} data-testid="onchain-back">
          Back
        </button>
      )}
      {state === 'paused' && (
        <>
          <Note data-testid="onchain-paused-note">Paused: no NFT is locked and you are not earning points. Lock an NFT to reactivate.</Note>
          <LockFlow kind="reactivate" wallet={wallet} nfts={nfts} levelPoint={lp} onDone={onDone} onBuy={onBuy} />
        </>
      )}
      {state === 'none' && (
        <>
          <Note>Lock one of your CADINU NFTs in the profile contract: it becomes your on-chain picture and starts your points. Pausing gives it back.</Note>
          <LockFlow kind="register" wallet={wallet} nfts={nfts} levelPoint={lp} onDone={onDone} onBuy={onBuy} />
        </>
      )}
    </section>
  )
}
